// Shared move-in/move-out protocol window + lockout logic (COL-424).
// Single source of truth for both the tenant (`ProtocolEntry`) and admin
// (`ProtocolCard`) surfaces — do not reimplement this date math elsewhere.

export const MOVE_IN_WINDOW_HOURS = 48;
export const MOVE_OUT_WINDOW_DAYS = 14;

export type ProtocolWindowInput = {
  moveInDate?: string | null;
  moveOutDate?: string | null;
  /** `bookings.move_in_protocol_reopened_at` */
  moveInReopenedAt?: string | null;
  /** `bookings.move_out_protocol_reopened_at` */
  moveOutReopenedAt?: string | null;
  /** Injectable for tests; defaults to `Date.now()`. */
  now?: number;
};

export type ProtocolWindowState =
  | "locked_before"
  | "open"
  | "reopened"
  | "expired";

export type ProtocolWindowStatus = {
  state: ProtocolWindowState;
  /** True while the tenant can submit right now (open or reopened). */
  canSubmit: boolean;
  /** True once the window has closed and no active reopen grace applies. */
  isExpired: boolean;
  availableFrom: string | null;
  closesAt: string | null;
};

/** The hour (Berlin local) the move-in protocol window opens on the move-in day. */
export const MOVE_IN_OPEN_HOUR_BERLIN = 2;

/**
 * Berlin's UTC offset, in minutes, at a given instant (+60 in winter, +120 in
 * summer). Derived from the IANA zone rather than hard-coded so the opening
 * hour below lands on the real local clock year-round.
 */
function berlinOffsetMinutes(at: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin",
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(at);
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asIfUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    // Some engines render midnight as hour 24 under hour12: false.
    get("hour") % 24,
    get("minute"),
    get("second")
  );
  return (asIfUtc - at.getTime()) / 60_000;
}

/**
 * The UTC instant of a Berlin wall-clock time.
 *
 * Two passes: the first guess treats the wall time as UTC and subtracts the
 * offset in force there; the second re-reads the offset at that candidate, so
 * a guess that landed on the wrong side of a DST switch corrects itself. On
 * the March switch day 02:00 Berlin does not exist (clocks jump 02:00 →
 * 03:00) and this resolves to 03:00 local, which is the first moment the
 * window can legitimately be open.
 */
function berlinWallClock(
  y: number,
  m: number,
  d: number,
  hour: number,
  minute = 0
): Date {
  const asIfUtc = Date.UTC(y, m - 1, d, hour, minute, 0);
  const firstPass = asIfUtc - berlinOffsetMinutes(new Date(asIfUtc)) * 60_000;
  return new Date(asIfUtc - berlinOffsetMinutes(new Date(firstPass)) * 60_000);
}

/**
 * Berlin midnight (00:01), approximated as a fixed UTC+1 offset — matches
 * `moveOutMoment` in
 * `supabase/functions/protocol-reminder-tick/window-helpers.ts`. Using the
 * same fixed-offset approximation (rather than the browser's local time,
 * which drifts by 1h across DST and by more outside UTC+1) keeps the client
 * and the cron job's auto-complete stamp agreeing on when a window closes.
 *
 * Still fixed-offset because the MOVE-OUT anchors below are defined as
 * "midnight-ish", where an hour either way is immaterial. The move-in opening
 * hour is a stated time of day, so it uses the real zone (`berlinWallClock`).
 */
function berlinMidnightPlusOne(y: number, m: number, d: number): Date {
  return new Date(Date.UTC(y, m - 1, d, 23, 1, 0));
}

function ymd(dateStr: string): [number, number, number] {
  return dateStr.split("-").map(Number) as [number, number, number];
}

/**
 * Start of move-in availability — 02:00 Berlin on the move-in day itself.
 *
 * Must stay identical to `windowStart` in
 * `supabase/functions/protocol-reminder-tick/window-helpers.ts`: the client
 * decides whether the tenant may submit, the cron decides when the window
 * lapsed, and the two disagreeing means a tenant is shown a form whose
 * submission the cron has already overwritten (or vice versa).
 */
export function moveInWindowStart(moveInDate: string): Date {
  const [y, m, d] = ymd(moveInDate);
  return berlinWallClock(y, m, d, MOVE_IN_OPEN_HOUR_BERLIN);
}

export function moveInWindowEnd(moveInDate: string): Date {
  return new Date(
    moveInWindowStart(moveInDate).getTime() +
      MOVE_IN_WINDOW_HOURS * 60 * 60 * 1000
  );
}

export function moveOutWindowStart(moveOutDate: string): Date {
  const [y, m, d] = ymd(moveOutDate);
  return berlinMidnightPlusOne(y, m, d - MOVE_OUT_WINDOW_DAYS - 1);
}

/** The move-out deadline: Berlin midnight (00:01) the day after `moveOutDate`. */
export function moveOutDeadline(moveOutDate: string): Date {
  const [y, m, d] = ymd(moveOutDate);
  return berlinMidnightPlusOne(y, m, d);
}

export function computeMoveInWindowStatus(
  input: ProtocolWindowInput
): ProtocolWindowStatus {
  const opensAt = input.moveInDate ? moveInWindowStart(input.moveInDate) : null;
  const closesAt = input.moveInDate ? moveInWindowEnd(input.moveInDate) : null;
  return computeWindowStatus({
    now: input.now ?? Date.now(),
    opensAt,
    closesAt,
    reopenedAt: input.moveInReopenedAt,
  });
}

export function computeMoveOutWindowStatus(
  input: ProtocolWindowInput
): ProtocolWindowStatus {
  const opensAt = input.moveOutDate
    ? moveOutWindowStart(input.moveOutDate)
    : null;
  const closesAt = input.moveOutDate
    ? moveOutDeadline(input.moveOutDate)
    : null;
  return computeWindowStatus({
    now: input.now ?? Date.now(),
    opensAt,
    closesAt,
    reopenedAt: input.moveOutReopenedAt,
  });
}

function computeWindowStatus(args: {
  now: number;
  opensAt: Date | null;
  closesAt: Date | null;
  reopenedAt: string | null | undefined;
}): ProtocolWindowStatus {
  const { now, opensAt, closesAt, reopenedAt } = args;

  if (reopenedAt) {
    return {
      state: "reopened",
      canSubmit: true,
      isExpired: false,
      availableFrom: null,
      closesAt: null,
    };
  }
  if (opensAt && now < opensAt.getTime()) {
    return {
      state: "locked_before",
      canSubmit: false,
      isExpired: false,
      availableFrom: opensAt.toISOString(),
      closesAt: closesAt ? closesAt.toISOString() : null,
    };
  }
  if (closesAt && now > closesAt.getTime()) {
    return {
      state: "expired",
      canSubmit: false,
      isExpired: true,
      availableFrom: null,
      closesAt: closesAt.toISOString(),
    };
  }
  return {
    state: "open",
    canSubmit: true,
    isExpired: false,
    availableFrom: null,
    closesAt: closesAt ? closesAt.toISOString() : null,
  };
}
